import React, { useMemo, useState } from 'react';
import { Button, Select, message as antMessage } from 'antd';
import { CheckCircleFilled, WarningFilled } from '@ant-design/icons';
import { post } from 'utils/fetch';
import styles from './index.module.css';
import BugHerdCircleLogo from '../../../assets/images/bugherd-logo-circle.svg';
import WhiteLogo from '../../../../../assets/images/icons/bugherd-white-logo.svg';

type Organization = {
  id: number;
  name: string;
};

type Permission = {
  id: string;
  title: string;
  description: string;
};

type AuthorizationRequest = {
  response_type: string;
  client_id: string;
  client_name?: string;
  redirect_uri: string;
  scope: string;
  state?: string;
  code_challenge: string;
  code_challenge_method: string;
};

type Props = {
  authenticityToken: string;
  endpoint: string;
  clientName: string;
  redirectUriAllowed: boolean;
  redirectUriHost?: string;
  redirectUriSupportEmail: string;
  organizations: Organization[];
  selectedOrganizationId?: number;
  permissions: Permission[];
  authorizationRequest: AuthorizationRequest;
  clientUri?: string | null;
  localhostWarning?: boolean;
};

export default function OauthAuthorize({
  endpoint,
  clientName,
  redirectUriAllowed,
  redirectUriHost,
  redirectUriSupportEmail,
  organizations,
  selectedOrganizationId,
  permissions,
  authorizationRequest,
  clientUri,
  localhostWarning,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [organizationId, setOrganizationId] = useState<number | undefined>(
    selectedOrganizationId || organizations[0]?.id
  );

  const selectOptions = useMemo(
    () => organizations.map(organization => ({
      value: organization.id,
      label: organization.name,
    })),
    [organizations]
  );

  const handleAllow = async () => {
    if (!organizationId) {
      antMessage.error('Please select an organization.', 5);
      return;
    }

    setLoading(true);

    try {
      const body = await post(endpoint, {
        ...authorizationRequest,
        organization_id: organizationId,
      });

      if (body.redirect_to) {
        window.location.href = body.redirect_to;
        window.setTimeout(() => {
          window.close();
        }, 5000);
        return;
      }

      throw new Error('Missing redirect target');
    } catch (error) {
      setLoading(false);
      antMessage.error('Unable to authorize access. Please try again.', 5);
    }
  };

  return (
    <div className={styles.page}>
      <WhiteLogo className={styles.wordmark} />

      <div className={styles.centered}>
        <div className={styles.card}>
          <div className={styles.intro}>
            <BugHerdCircleLogo className={styles.icon} />
            <h1 className={styles.title}>BugHerd MCP</h1>
            <p className={styles.subtitle}>
              <strong>{clientName}</strong>{' '}
              wants to access your BugHerd account
            </p>
            {clientUri && (
              <p className={styles.clientUri}>{clientUri}</p>
            )}
          </div>

          {redirectUriAllowed ? (
            <>
              {localhostWarning && (
                <div className={styles.localhostWarning}>
                  <WarningFilled className={styles.warningIcon} />
                  <p className={styles.warningText}>
                    This client uses a localhost redirect URI, which means any
                    application on your computer could intercept the
                    authorization. Only proceed if you trust the application.
                  </p>
                </div>
              )}

              <div className={styles.permissionsPanel}>
                <p className={styles.permissionsIntro}>This will allow {clientName} to:</p>

                <div className={styles.permissionsList}>
                  {permissions.map(permission => (
                    <div className={styles.permission} key={permission.id}>
                      <CheckCircleFilled className={styles.permissionIcon} />
                      <div>
                        <p className={styles.permissionTitle}>{permission.title}</p>
                        <p className={styles.permissionDescription}>
                          {permission.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {organizations.length > 1 && (
                <div className={styles.organizationSection}>
                  <p className={styles.organizationLabel}>Select organization to access:</p>

                  <Select
                    className={styles.select}
                    value={organizationId}
                    options={selectOptions}
                    onChange={value => setOrganizationId(value)}
                    size="large"
                  />
                </div>
              )}

              <Button
                className={styles.allowButton}
                type="primary"
                size="large"
                block
                loading={loading}
                onClick={handleAllow}
              >
                Allow
              </Button>

              <p className={styles.disclaimer}>
                By clicking &quot;Allow&quot;, you authorize {clientName} to access your
                BugHerd data with the permissions listed above, and confirm that you have
                read and agree to our{' '}
                <a href="https://bugherd.com/terms" target="_blank" rel="noopener">
                  terms of service
                </a>{' '}
                and{' '}
                <a href="https://bugherd.com/privacy" target="_blank" rel="noopener">
                  privacy policy
                </a>
                .
              </p>
            </>
          ) : (
            <>
              <div className={styles.blockedPanel}>
                <p className={styles.blockedTitle}>
                  This redirect URI is not currently supported.
                </p>
                <p className={styles.blockedDescription}>
                  BugHerd can only authorize MCP clients that use an approved redirect
                  host.
                </p>
                {redirectUriHost && (
                  <p className={styles.blockedDescription}>
                    Requested host: <strong>{redirectUriHost}</strong>
                  </p>
                )}
                <p className={styles.blockedDescription}>
                  If you believe this redirect URI should be supported,{' '}
                  <a href={`mailto:${redirectUriSupportEmail}`}>
                    contact BugHerd support
                  </a>
                  .
                </p>
              </div>

              <Button
                className={styles.allowButton}
                type="primary"
                size="large"
                block
                disabled
              >
                Allow
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
